import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { PlatformLogo } from "@/components/PlatformLogo";
import { Skeleton } from "@/components/Skeleton";

/* Admin → Parsers — live health metrics for the ssparser / mhparser scraper
 * services (GET /api/admin/parsers/metrics, 25s server cache, 30s poll). */

interface HourPoint { hour: string; count: number }
interface DayPoint { day: string; count: number }

interface ParserMetrics {
  error: string | null;
  status: "live" | "lagging" | "stalled" | null;
  newest_ingest_at: string | null;
  last_poll_at: string | null;
  tip_id: number | null;
  total_listings: number;
  total_phones: number;
  retention_from: string | null;
  retention_to: string | null;
  posted_today_ingested: number;
  ingested_today: number;
  hourly: HourPoint[];
  coverage: DayPoint[];
  queue: Record<string, number>;
  oldest_pending_age_s: number | null;
  sellers: Record<string, number>;
  extra: Record<string, unknown>;
}

interface MetricsResponse {
  generated_at: string;
  cached: boolean;
  myhome: ParserMetrics;
  ss: ParserMetrics;
}

const POLL_MS = 30_000;

function ago(s: string | null): string {
  if (!s) return "—";
  const secs = Math.max(0, (Date.now() - new Date(s).getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)}m ago`;
  if (secs < 129600) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function fmtInt(n: number | null | undefined): string {
  return n == null ? "—" : n.toLocaleString();
}

function fmtDur(s: number | null): string {
  if (s == null) return "—";
  if (s < 120) return `${Math.round(s)}s`;
  if (s < 7200) return `${Math.round(s / 60)}m`;
  return `${Math.round(s / 3600)}h`;
}

export function AdminParsers() {
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  // This endpoint scans both scraper tables, so a slow response can outlast the
  // poll interval. Without a guard each tick would stack another concurrent
  // request, and the pile-up is what makes the endpoint slower still.
  const inFlight = useRef(false);
  const abort = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    try {
      const r = await api<MetricsResponse>("/api/admin/parsers/metrics", {
        signal: ctl.signal,
      });
      setData(r);
      setErr(null);
    } catch (e) {
      if (ctl.signal.aborted) return; // unmounted or superseded — not an error
      setErr(
        e instanceof ApiError && e.status === 403
          ? "You don't have access to this page."
          : e instanceof Error
            ? e.message
            : "Failed to load metrics",
      );
    } finally {
      inFlight.current = false;
      if (!ctl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    timer.current = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
      abort.current?.abort();
    };
  }, [load]);

  return (
    <div className="w-full space-y-5">
      <PageHeader
        title="Parsers"
        subtitle="Live health of the ssparser / mhparser scraper services — ingestion, backfill and queue state."
        actions={
          <button
            type="button"
            onClick={() => { setLoading(true); void load(); }}
            className="btn-secondary !px-3.5 !py-2 !text-xs"
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        }
      />

      {err && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {err}
        </div>
      )}

      {!data && !err && (
        <div className="space-y-4">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      )}

      {data && (
        <>
          <div className="text-[11px] text-slate-600 px-1 -mt-2">
            updated {ago(data.generated_at)}
            {data.cached && " · cached"} · auto-refreshes every 30s
          </div>
          <ParserSection name="MyHome" portal="myhome" m={data.myhome} />
          <ParserSection name="SS.ge" portal="ss" m={data.ss} />
        </>
      )}
    </div>
  );
}

/* ── One parser ──────────────────────────────────────────────────────────── */

const STATUS_PILL: Record<string, { cls: string; label: string }> = {
  live: { cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400", label: "Live" },
  lagging: { cls: "border-amber-500/30 bg-amber-500/10 text-amber-300", label: "Lagging" },
  stalled: { cls: "border-rose-500/30 bg-rose-500/10 text-rose-300", label: "Stalled" },
};

function ParserSection({
  name,
  portal,
  m,
}: {
  name: string;
  portal: "myhome" | "ss";
  m: ParserMetrics;
}) {
  if (m.error) {
    return (
      <div className="card overflow-hidden">
        <SectionHead name={name} portal={portal} m={m} />
        <div className="px-5 py-6 text-[13px] text-rose-300">{m.error}</div>
      </div>
    );
  }

  const backlog = (m.queue["pending"] ?? 0) + (m.queue["in_progress"] ?? 0);

  return (
    <div className="card overflow-hidden">
      <SectionHead name={name} portal={portal} m={m} />

      {/* Stat band */}
      <div className="grid grid-cols-2 sm:grid-cols-5 border-y border-white/5 [&>*]:border-white/5 sm:[&>*:not(:first-child)]:border-l [&>*:last-child]:col-span-2 sm:[&>*:last-child]:col-span-1">
        <Stat label="Total listings" value={fmtInt(m.total_listings)} />
        <Stat label="Ingested today" value={fmtInt(m.ingested_today)} />
        <Stat label="Posted today · in" value={fmtInt(m.posted_today_ingested)} />
        <Stat
          label="Queue backlog"
          value={fmtInt(backlog)}
          sub={m.oldest_pending_age_s != null ? `oldest ${fmtDur(m.oldest_pending_age_s)}` : undefined}
          warn={backlog > 5000}
        />
        <Stat label="Phones" value={fmtInt(m.total_phones)} />
      </div>

      {/* Charts */}
      <div className="grid gap-5 lg:grid-cols-2 px-5 py-4">
        <MiniBars
          title="Ingest rate — last 24h"
          points={fillHourly(m.hourly)}
        />
        <MiniBars
          title="Coverage by posted day — last 14d"
          points={fillDaily(m.coverage)}
        />
      </div>

      {/* Queue + crawl state + sellers */}
      <div className="grid gap-4 lg:grid-cols-3 px-5 pb-5">
        <MetaCard title="Queue">
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(m.queue)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <span key={k} className={`badge whitespace-nowrap ${queueTone(k)}`}>
                  {k} · {fmtInt(v)}
                </span>
              ))}
            {Object.keys(m.queue).length === 0 && (
              <span className="text-[11.5px] text-slate-600">empty</span>
            )}
          </div>
        </MetaCard>

        <MetaCard title="Crawl state">
          <div className="space-y-1 text-[11.5px]">
            <MetaRow k="Tip id" v={m.tip_id != null ? String(m.tip_id) : "—"} mono />
            <MetaRow k="Last poll" v={ago(m.last_poll_at)} />
            <MetaRow k="Newest ingest" v={ago(m.newest_ingest_at)} />
            {portal === "myhome" ? (
              <>
                <MetaRow
                  k="Full sweep"
                  v={m.extra["last_full_sweep_at"] ? ago(String(m.extra["last_full_sweep_at"])) : "—"}
                />
                <MetaRow
                  k="Backfill"
                  v={m.extra["backfill_complete"] ? "complete ✓" : "in progress…"}
                />
                <MetaRow k="Sweep mode" v={String(m.extra["sweep_mode"] ?? "—")} />
              </>
            ) : (
              <>
                <MetaRow k="Inactive" v={fmtInt(m.extra["inactive"] as number)} />
                <MetaRow k="Owner profiles" v={fmtInt(m.extra["owner_profiles"] as number)} />
                <MetaRow
                  k="Retention"
                  v={
                    m.retention_from
                      ? `${new Date(m.retention_from).toLocaleDateString(undefined, { day: "numeric", month: "short" })} →`
                      : "—"
                  }
                />
              </>
            )}
          </div>
        </MetaCard>

        <MetaCard title="Sellers">
          <SellerBars sellers={m.sellers} />
        </MetaCard>
      </div>
    </div>
  );
}

function SectionHead({
  name,
  portal,
  m,
}: {
  name: string;
  portal: "myhome" | "ss";
  m: ParserMetrics;
}) {
  const pill = m.status ? STATUS_PILL[m.status] : null;
  return (
    <div className="px-5 py-4 flex items-center gap-3 flex-wrap">
      <div className="w-16 flex items-center">
        <PlatformLogo platform={portal} size="sm" />
      </div>
      <span className="text-[15px] font-semibold text-slate-100">{name} parser</span>
      {pill && (
        <span className={`badge ${pill.cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
            m.status === "live" ? "bg-emerald-400" : m.status === "lagging" ? "bg-amber-400" : "bg-rose-400"
          }`} />
          {pill.label}
        </span>
      )}
      {m.error && (
        <span className="badge border-rose-500/30 bg-rose-500/10 text-rose-300">unreachable</span>
      )}
      <span className="ml-auto text-[11px] text-slate-600">
        last poll {ago(m.last_poll_at)}
      </span>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  warn = false,
}: {
  label: string;
  value: string;
  sub?: string;
  warn?: boolean;
}) {
  return (
    <div className="px-5 py-3.5 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className={`mt-1 text-[16px] font-semibold tabular-nums truncate ${warn ? "text-amber-300" : "text-slate-100"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-600 tabular-nums">{sub}</div>}
    </div>
  );
}

function MetaCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.015] px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function MetaRow({ k, v, mono = false }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-slate-500">{k}</span>
      <span className={`text-slate-300 ${mono ? "font-mono tabular-nums text-[11px]" : ""} truncate`}>{v}</span>
    </div>
  );
}

function queueTone(status: string): string {
  switch (status) {
    case "done":
      return "border-emerald-500/25 bg-emerald-500/[0.07] text-emerald-300";
    case "pending":
    case "in_progress":
      return "border-accent-500/30 bg-accent-500/10 text-accent-300";
    case "failed":
      return "border-rose-500/30 bg-rose-500/10 text-rose-300";
    case "deleted":
      return "border-slate-500/30 bg-slate-500/10 text-slate-400";
    default:
      return "border-white/10 bg-white/[0.02] text-slate-400";
  }
}

/* ── Charts (hand-rolled; palette validated: accent bars + emerald-600 today) ── */

interface BarPoint {
  label: string; // tooltip label
  tick?: string; // sparse x-axis tick under the bar
  value: number;
  highlight?: boolean; // "today" — emerald-600 + direct label
}

function fillHourly(points: HourPoint[]): BarPoint[] {
  const by = new Map(points.map((p) => [new Date(p.hour).getTime(), p.count]));
  const out: BarPoint[] = [];
  const now = new Date();
  now.setMinutes(0, 0, 0);
  for (let i = 23; i >= 0; i--) {
    const t = new Date(now.getTime() - i * 3600_000);
    const hh = t.getHours().toString().padStart(2, "0");
    out.push({
      label: `${hh}:00 — ${by.get(t.getTime()) ?? 0} ingested`,
      tick: i === 23 || i === 12 || i === 0 ? `${hh}:00` : undefined,
      value: by.get(t.getTime()) ?? 0,
    });
  }
  return out;
}

function fillDaily(points: DayPoint[]): BarPoint[] {
  const by = new Map(points.map((p) => [p.day, p.count]));
  const out: BarPoint[] = [];
  const today = new Date();
  for (let i = 13; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86400_000);
    const iso = d.toISOString().slice(0, 10);
    const nice = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    out.push({
      label: `${nice} — ${by.get(iso) ?? 0} listings`,
      tick: i === 13 || i === 7 ? nice : i === 0 ? "today" : undefined,
      value: by.get(iso) ?? 0,
      highlight: i === 0,
    });
  }
  return out;
}

function MiniBars({ title, points }: { title: string; points: BarPoint[] }) {
  const max = Math.max(1, ...points.map((p) => p.value));
  const peakIdx = points.findIndex((p) => p.value === max);
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          {title}
        </span>
      </div>
      <div className="flex items-end gap-[2px] h-24 border-b border-white/10">
        {points.map((p, i) => {
          const hPct = Math.max(p.value > 0 ? 4 : 1, (p.value / max) * 100);
          return (
            <div key={i} className="relative group flex-1 h-full flex flex-col justify-end min-w-0">
              {/* peak gets a persistent direct label; every bar labels on hover */}
              {(i === peakIdx && p.value > 0) && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] tabular-nums text-slate-500 whitespace-nowrap">
                  {p.value.toLocaleString()}
                </span>
              )}
              <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 z-10 hidden group-hover:block rounded-md bg-black/85 px-2 py-1 text-[10px] text-white whitespace-nowrap">
                {p.label}
              </span>
              <div
                className={`w-full rounded-t-[3px] transition-colors ${
                  p.highlight
                    ? "bg-emerald-600 group-hover:bg-emerald-500"
                    : "bg-accent-500/80 group-hover:bg-accent-400"
                }`}
                style={{ height: `${hPct}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1">
        {points.map((p, i) =>
          p.tick ? (
            <span
              key={i}
              className={`text-[9px] tabular-nums ${p.highlight ? "text-emerald-400 font-semibold" : "text-slate-600"}`}
            >
              {p.tick}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}

function SellerBars({ sellers }: { sellers: Record<string, number> }) {
  const entries = Object.entries(sellers).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (entries.length === 0) {
    return <span className="text-[11.5px] text-slate-600">no data</span>;
  }
  return (
    <div className="space-y-1.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="w-28 flex-shrink-0 text-[10.5px] text-slate-500 truncate" title={k}>
            {k}
          </span>
          <div className="flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
            <div
              className="h-full rounded-full bg-accent-500/70"
              style={{ width: `${(v / max) * 100}%` }}
            />
          </div>
          <span className="w-14 text-right text-[10.5px] tabular-nums text-slate-400">
            {v.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}
